import React, { useState } from 'react';
import Router from 'next/router';
import { gql } from '@soundxyz/gql-string';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { StandardModalButton } from '@components/main-components/modals/StandardModalButton';
import { useAuth } from '@context/auth/base';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  AllUserPlaylistsDocument,
  DeleteShelfDocument,
  LeftNavPlaylistsDocument,
  RecoverPlaylistDocument,
  useMutation,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation RecoverPlaylist($playlistId: UUID!) {
    recoverShelf(deletedShelfId: $playlistId) {
      __typename
      ... on MutationRecoverShelfSuccess {
        data {
          id
        }
      }
    }
  }
`);

RefetchOnComplete({
  trigger: [DeleteShelfDocument, RecoverPlaylistDocument],
  refetch: [LeftNavPlaylistsDocument, AllUserPlaylistsDocument],
});

export const DeleteShelfModal = ({ shelfId }: { shelfId: string }) => {
  const { closeCancelModal } = useModal();
  const { openToast } = useToast();

  const { authUser } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutateAsync: deleteShelf } = useMutation(DeleteShelfDocument, {});
  const { mutateAsync: recoverPlaylist } = useMutation(RecoverPlaylistDocument, {});

  if (!authUser) return null;

  const handleUndo = async () => {
    try {
      const recoverData = await recoverPlaylist({
        playlistId: shelfId,
      });
      if (recoverData?.data?.recoverShelf?.__typename === 'MutationRecoverShelfSuccess') {
        openToast({ text: 'Your playlist has been recovered', variant: 'success' });
      } else {
        // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
        openToast({ text: 'Error recovering your playlist.', variant: 'error' });
      }
    } catch (error) {
      // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
      openToast({ text: 'Error recovering your playlist.', variant: 'error' });
    }
  };

  const bodyText = (
    <>
      <p>Are you sure you want to delete this playlist?</p>
      <p>Once deleted, this cannot be undone but you can always create a new playlist.</p>
    </>
  );

  return (
    <StandardModal
      title="Delete Playlist"
      body={bodyText}
      bodyWithoutPaddingBottom
      callToAction={
        <StandardModalButton
          label="Delete my playlist"
          variant="critical"
          loading={isDeleting}
          onClick={async () => {
            setIsDeleting(true);
            try {
              await deleteShelf({
                input: {
                  shelfId,
                },
              });

              const playlistsUserPath = webappUriPath(authUser.webappUri, {
                subpath: '/playlists',
              });

              if (!window.location.pathname.startsWith(playlistsUserPath)) {
                await Router.push(playlistsUserPath);
              }

              openToast({
                text: 'Your playlist has been deleted.',
                ctaText: 'Undo',
                ctaOnClick: handleUndo,
              });
            } catch (error) {
              // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
              openToast({ text: 'Error deleting your playlist.', variant: 'error' });
            } finally {
              closeCancelModal();
            }
          }}
        />
      }
      backCallToAction={
        <StandardModalButton label="Cancel" variant="secondary" onClick={closeCancelModal} />
      }
      closeModal={closeCancelModal}
    />
  );
};
